import clsx from 'clsx';
import React, { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from 'react-oidc-context';
import Header from './components/Header';
import LeftBar from './components/LeftBar';
import { Loader } from './components/Loader/Loader';
import Route from './components/Route';
import Sidebar from './components/Sidebar';
import StepIndicator from './components/StepIndicator';
import FlashToast from './components/syncunit/FlashToast';
import {
  ENABLE_INVESTING_ACCOUNT_CONTROLLER,
  ENABLE_PROFILE,
  ENABLE_REFERRAL_CONTROLLER,
  ENABLE_SYNC_UNIT_CONTROLLER,
  ENABLE_TAX_DECLARATION_CONTROLLER,
  ENABLE_TAX_PLANNER_CONTROLLER,
  ENABLE_UNIFIED_UPLOAD,
} from './constants/constants';
import { INVESTING_ACCOUNT_ROUTE, SYNC_UNIT_ROUTE, SYNC_UNITS_PATH } from './constants/syncRoutes';
import useNavigation from './hooks/use-navigation';
import useToken from './hooks/use-token';
import AccountsPage from './pages/AccountsPage';
import AutoSyncPage from './pages/AutoSyncPage';
import { CabinetPage } from './pages/CabinetPage';
// import DeclarationPage from './pages/DeclarationPage';
import { ErrorBoundaryWrapper } from './components/ErrorBounder';
import HomePage from './pages/HomePage';
import IncomesPage from './pages/IncomesPage';
import InvestingAccountPage from './pages/InvestingAccountPage';
import ProfilePage from './pages/ProfilePage';
import ReferralInfoPage from './pages/ReferralInfoPage';
import ReferralPage from './pages/ReferralPage';
import ReportsPage from './pages/ReportsPage';
import SettingsPage from './pages/SettingsPage';
import SyncUnitPage from './pages/SyncUnitPage';
import TaxPlannerPage from './pages/TaxPlannerPage';
import TaxReportsPage from './pages/taxreports/TaxReportsPage';
import UnifiedUploadPage, { isUnifiedUploadPath, UNIFIED_ACCOUNTS_PATH } from './pages/UnifiedUploadPage';
import UnifiedUploadedAccountsPage from './pages/UnifiedUploadedAccountsPage';
import UploadPage from './pages/UploadPage';
import { useBecomeReferredMutation } from './store';

function App() {
  const auth = useAuth();
  const { t } = useTranslation();
  const token = useToken();
  const [becomeReferred] = useBecomeReferredMutation();
  const url = new URL(window.location.href);
  const referral = url.searchParams.get('referral');
  const referralCode = JSON.parse(localStorage.getItem('referral'));
  if (referral && !referralCode) {
    localStorage.setItem('referral', JSON.stringify(referral));
  }

  const { currentPath } = useNavigation();

  // One POST per entry: a silent token renew re-renders App and must not repeat it (B2-02).
  const referred = useRef(false);
  useEffect(() => {
    if (referralCode && token && !referred.current) {
      referred.current = true;
      becomeReferred({ referralCode }).then(() => {
        localStorage.removeItem('referral');
        window.location = '/';
      });
    }
  }, [becomeReferred, token, referralCode]);

  useEffect(() => {
    if (auth.error) console.error('Sign-in renew failed', auth.error);
  }, [auth.error]);

  switch (auth.activeNavigator) {
    case 'signinSilent':
      return <div></div>; // Входимо...
    case 'signoutRedirect':
      return <div></div>; // Виходимо...
    default:
    // do nothing
  }

  if (auth.isLoading) {
    return <Loader />; // Завантажується...
  }

  // A failed silent renew while the session is still valid only warns: unmounting the routes would re-read every
  // page after a reload (B2-04). With the session over, the blocking screen stays.
  if (auth.error && !auth.isAuthenticated) {
    return <div>Oops... {auth.error.message}</div>;
  }

  if (auth.isAuthenticated) {
    // http://localhost:3000?referral=dd943074-5410-4264-b2ec-64646fee6d18
    // console.log("referral ", referral, auth.user?.profile?.sub); xs:overflow-hidden  h-screen xs:max-h-screen
    return (
      <ErrorBoundaryWrapper>
        <div className={clsx(
          "lg:mx-auto flex lg:min-h-[520px] overflow-hidden h-screen",
          ENABLE_SYNC_UNIT_CONTROLLER && "flex-col md:flex-row bg-bg",
        )}>
          <LeftBar />
          <div className={clsx(
            // relative: an absolutely positioned descendant stays inside the scroller instead of growing the document (B2-05)
            "relative w-full min-h-screen grid grid-rows-[auto_1fr_auto] h-screen overflow-auto", //xs:h-full
            currentPath === '/' && 'bg-gradient-to-r from-cyan-650 to-cyan-75',
            // 'xs:pb-5'
          )}
          >
            {ENABLE_SYNC_UNIT_CONTROLLER ? (
              <div>
                <StepIndicator />
              </div>
            ) : (
              <div className="block lg:hidden z-10">
                {/* <div className='xs:fixed xs:top-0 xs:w-full xs:z-10'> xs:overflow-hidden */}
                  <Header />
                {/* </div> xs:overflow-auto */}
              </div>
            )}
            <div className="xs:pb-24">
              <Route path="/">
                <HomePage />
              </Route>
              {ENABLE_INVESTING_ACCOUNT_CONTROLLER && (
                <>
                  <Route path="/accounts">
                    <AccountsPage />
                  </Route>
                  <Route path="/autosync">
                    <AutoSyncPage />
                  </Route>
                  <Route path="/upload">
                    <UploadPage />
                  </Route>
                  <Route path="/reports">
                    <ReportsPage />
                  </Route>
                </>
              )}
              {ENABLE_UNIFIED_UPLOAD && ENABLE_INVESTING_ACCOUNT_CONTROLLER && isUnifiedUploadPath(currentPath) && (
                currentPath === UNIFIED_ACCOUNTS_PATH ? <UnifiedUploadedAccountsPage /> : <UnifiedUploadPage />
              )}
              {ENABLE_SYNC_UNIT_CONTROLLER && ENABLE_INVESTING_ACCOUNT_CONTROLLER && (
                <>
                  <Route path={SYNC_UNITS_PATH}>
                    <UnifiedUploadedAccountsPage />
                  </Route>
                  <Route path={SYNC_UNIT_ROUTE}>
                    <SyncUnitPage />
                  </Route>
                  <Route path={INVESTING_ACCOUNT_ROUTE}>
                    <InvestingAccountPage />
                  </Route>
                </>
              )}
              {ENABLE_TAX_DECLARATION_CONTROLLER && (
                <>
                  {/* <Route path="/declaration">
                    <DeclarationPage />
                  </Route> */}
                  <Route path="/taxreports">
                    <TaxReportsPage />
                  </Route>
                </>
              )}
              {ENABLE_TAX_PLANNER_CONTROLLER && (
                <Route path="/tax-planner">
                  <TaxPlannerPage />
                </Route>
              )}
              {ENABLE_REFERRAL_CONTROLLER && (
                <>
                  <Route path="/referral">
                    <ReferralPage />
                  </Route>
                  <Route path="/referral-info">
                    <ReferralInfoPage />
                  </Route>
                </>
              )}
              {
                ENABLE_PROFILE && (
                  <>
                    <Route path="/cabinet">
                      <CabinetPage />
                    </Route>
                    <Route path="/profile">
                      <ProfilePage />
                    </Route>
                    <Route path="/incomes">
                      <IncomesPage />
                    </Route>
                  </>
                )
              }
              <Route path="/settings">
                <SettingsPage />
              </Route>
              {ENABLE_SYNC_UNIT_CONTROLLER && <FlashToast />}
              {auth.error && (
                <div
                  role="status"
                  className="fixed top-4 left-1/2 -translate-x-1/2 z-50 rounded-xl bg-[#14171a] px-4 py-3 text-sm text-white shadow-lg"
                >
                  {t('renewFailed')}
                </div>
              )}
            </div>
            {/* style={{ marginBottom: 'var(--mb)'}} */}
            {ENABLE_SYNC_UNIT_CONTROLLER ? (
              <div />
            ) : (
              <div className="block lg:hidden"> 
                <div 
                  className='block xs:fixed xs:bottom-0 xs:z-10 xs:w-full' 
                  style={{
                    bottom: 'constant(safe-area-inset-bottom)',
                    // eslint-disable-next-line no-dupe-keys -- intentional CSS fallback pair
                    bottom: 'env(safe-area-inset-bottom)',
                  }}
                > 
                  <Sidebar />
                </div>
              </div>
            )}
          </div>
        </div>
      </ErrorBoundaryWrapper>
    );
  }

  return void auth.signinRedirect();
}

export default App;
